import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/cn";

/** As colunas do arquivo: marcar o que sai e arrastar pra mudar a ordem. */
export function ListaDeColunas({
  titulos,
  ocultas,
  onOrdem,
  onAlternar,
}: {
  titulos: string[];
  ocultas: string[];
  onOrdem: (ordem: string[]) => void;
  onAlternar: (titulo: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const aoSoltar = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    onOrdem(arrayMove(titulos, titulos.indexOf(String(e.active.id)), titulos.indexOf(String(e.over.id))));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={aoSoltar}>
      <SortableContext items={titulos} strategy={verticalListSortingStrategy}>
        <ul className="space-y-1">
          {titulos.map((t) => (
            <Linha key={t} titulo={t} marcada={!ocultas.includes(t)} onAlternar={() => onAlternar(t)} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function Linha({ titulo, marcada, onAlternar }: { titulo: string; marcada: boolean; onAlternar: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: titulo });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-1 rounded-md border border-zinc-200 bg-white pr-2 dark:border-border dark:bg-card",
        isDragging && "relative z-10 shadow-md",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Arrastar ${titulo}`}
        className="flex h-9 w-8 shrink-0 cursor-grab touch-none items-center justify-center text-zinc-400 hover:text-zinc-600 active:cursor-grabbing dark:text-muted-foreground/60 dark:hover:text-foreground"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-2">
        <input type="checkbox" checked={marcada} onChange={onAlternar} className="h-4 w-4 shrink-0 rounded border-zinc-300 text-brand-600" />
        <span className={cn("truncate text-[13px]", marcada ? "text-zinc-800 dark:text-foreground" : "text-zinc-400 dark:text-muted-foreground")}>{titulo}</span>
      </label>
    </li>
  );
}
